import { DeleteTwoTone, EditOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import { Affix, Button, Card, Dropdown, Menu, message, Popconfirm, Space } from "antd";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LINE_WISE_OPERATIONS_ENDPOINT } from "../../apiServices/API_ENDPOINTS";
import { deleteData, getData } from "../../apiServices/common";
import moment from "moment";
import ListTable from "../../common/ListTable";
import {
  FOLDING_REQUIREMENT_CREATE_PERMISSION,
  FOLDING_REQUIREMENT_EDIT_PERMISSION,
  FOLDING_REQUIREMENT_VIEW_PERMISSION,
} from "../../routes/permissions";
import { hasPermission } from "../../utils/functions";
import { isMobileView } from "../Common/AppHelper";
import CustomFilter from "../Common/CustomFilter";
import Header from "../Common/Header";

const HourlyOperationTracking = ({ width }) => {
  // Router
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location?.pathname;

  // States
  const [showFilter, setShowFilter] = useState(() => {
    const filterStatus = localStorage.getItem("filter_status");
    return filterStatus && routerPath ? JSON.parse(filterStatus)[routerPath] : false;
  });
  const [listData, setListData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [rangeData, setRangeData] = useState(10);
  const lastFilterRef = useRef({ filter: {}, isSearch: false });

  // Fetch List Data
  const getListData = useCallback(async (filter = {}, isSearch = false) => {
    lastFilterRef.current = { filter, isSearch };

    // For search: both query_date and line_id are required
    // For default load: only query_date is required (with current date)
    if (isSearch) {
      // When searching, both are required
      if (!filter?.query_date || !filter?.line_id) {
        setListData([]);
        setTotalData(0);
        setCurrentPage(1);
        return;
      }
    } else {
      // For default load, only date is required
      if (!filter?.query_date) {
        setListData([]);
        setTotalData(0);
        setCurrentPage(1);
        return;
      }
    }

    setLoading(true);
    setFetchError(null);
    try {
      const response = await getData(LINE_WISE_OPERATIONS_ENDPOINT, false, filter);
      if (response?.data?.code === 200) {
        const masterData = response.data;
        // API returns data directly, not paginated
        const dataArray = Array.isArray(masterData?.data) ? masterData.data : [];
        setListData(dataArray);
        setTotalData(dataArray.length);
        setRangeData(10);
      } else {
        setFetchError("Could not load this list.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setFetchError("Could not load this list.");
    } finally {
      setLoading(false);
    }
  }, []);

  const retryGetListData = useCallback(() => {
    const { filter, isSearch } = lastFilterRef.current;
    getListData(filter, isSearch);
  }, [getListData]);

  // Wrapper for search - requires both date and line_id
  // If filter is empty (clear button), reset to default (current date)
  const handleSearch = useCallback(
    async (filter = {}) => {
      // If filter is empty (clear button clicked), reset to default with current date
      if (!filter || Object.keys(filter).length === 0) {
        const currentDate = moment().format("YYYY-MM-DD");
        await getListData({ query_date: currentDate }, false);
      } else {
        // For search, both date and line_id are required
        await getListData(filter, true);
      }
    },
    [getListData],
  );

  // Handle Delete
  const handleDelete = useCallback(
    async (id) => {
      if (!id) return;
      try {
        const query = `${LINE_WISE_OPERATIONS_ENDPOINT}/${id}`;
        const response = await deleteData(query);
        if (response?.code === 200) {
          message.success("Data Deleted Successfully");
          // Refresh with current date after deletion
          const currentDate = moment().format("YYYY-MM-DD");
          getListData({ query_date: currentDate }, false);
        }
      } catch (error) {
        console.error("Error deleting data:", error);
      }
    },
    [getListData],
  );

  // Menu for Actions
  const menu = useCallback(
    (data) => (
      <Menu
        items={[
          {
            label: (
              <Button
                icon={<EyeOutlined />}
                type="text"
                size="small"
                onClick={() => navigate(`/digitalization/hourly-operation-tracking/view/${data?.id}`)}
              >
                View
              </Button>
            ),
            key: "1",
            disabled: !hasPermission([FOLDING_REQUIREMENT_VIEW_PERMISSION]),
          },
          {
            label: (
              <Button
                icon={<EditOutlined />}
                type="text"
                size="small"
                onClick={() => navigate(`/digitalization/hourly-operation-tracking/edit/${data?.id}`)}
              >
                Edit
              </Button>
            ),
            key: "12",
            disabled: !hasPermission([FOLDING_REQUIREMENT_EDIT_PERMISSION]),
          },
          {
            label: (
              <Popconfirm title="Are you sure you want to delete?" onConfirm={() => handleDelete(data?.id)}>
                <Button size="small" icon={<DeleteTwoTone />} danger type="text">
                  Delete
                </Button>
              </Popconfirm>
            ),
            key: "2",
          },
        ]}
      />
    ),
    [handleDelete, navigate],
  );

  // Memoized Columns
  const columns = useMemo(
    () => [
      {
        title: "Line Name",
        dataIndex: ["line_info", "name"],
        key: "line_name",
      },
      {
        title: "Query Date",
        dataIndex: "query_date",
        key: "query_date",
      },
      {
        title: "Operations Count",
        key: "operations_count",
        render: (_, record) => {
          return record?.line_wise_operation_details?.length || 0;
        },
      },
      {
        title: "Actions",
        key: "action",
        width: "10%",
        render: (_, record) => (
          <div className="cursor-pointer">
            <Dropdown overlay={menu(record)}>
              <MoreOutlined style={{ fontSize: "14px" }} />
            </Dropdown>
          </div>
        ),
      },
    ],
    [menu],
  );

  // Pagination Handler
  const paginate = useCallback((page) => setCurrentPage(page), []);

  // Memoized Filter
  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        tableData={listData}
        fetchTableData={handleSearch}
        filterName="hourly_operation_tracking"
      />
    ),
    [showFilter],
  );

  // Update Filter Status in LocalStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};
    parsedFilterStatus[routerPath] = showFilter;
    localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
  }, [showFilter, routerPath]);

  // Fetch data on mount with current date
  useEffect(() => {
    const currentDate = moment().format("YYYY-MM-DD");
    getListData({ query_date: currentDate }, false);
  }, [getListData]);

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card className="p-fixed" style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}>
          <Header
            title="Hourly Operation Tracking"
            subTitle=""
            actions={
              <Space>
                <Button
                  type={showFilter ? "danger" : "primary"}
                  size="small"
                  onClick={() => setShowFilter((prev) => !prev)}
                >
                  {`${showFilter ? "Close" : "Open"} Filter Option`}
                </Button>
                <Button
                  key="3"
                  size="small"
                  type="primary"
                  disabled={!hasPermission([FOLDING_REQUIREMENT_CREATE_PERMISSION])}
                >
                  <Link to="/digitalization/hourly-operation-tracking/create">
                    {hasPermission([FOLDING_REQUIREMENT_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
                  </Link>
                </Button>
              </Space>
            }
          />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          {memoizedCustomFilter}
          <ListTable
            loading={loading}
            error={fetchError}
            onRetry={() => retryGetListData()}
            emptyText="No records to show yet."
            dataSource={listData}
            columns={columns}
            scroll={{ x: true }}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: paginate,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              hideOnSinglePage: totalData <= rangeData,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
};

export default HourlyOperationTracking;
